import { CargoEvent, cargoEventsT } from "./types";
import * as tPromise from "io-ts-promise";
import axios, { getData } from "../../../axios";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { createSlice } from "@reduxjs/toolkit";
import { updateDriverEventT } from "dora-contracts";
import { handleCargoDeletedWhileLoading } from "../../app/cargo-dialog";

const prefix = "data/cargo-events";

type State = {
  entities: Record<string, CargoEvent[]>;
  requestStatus: Record<string, "loading" | "error" | undefined>;
};

const initialState: State = {
  entities: {},
  requestStatus: {},
};

export const loadCargoEvents = createErrorReportingAsyncThunk(
  `${prefix}/load-cargo-events`,
  async (cargoId: string, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    const requestStatus = getState().data.cargoEvents.requestStatus[cargoId];
    if (requestStatus === "loading") {
      return null;
    }
    dispatch(loadCargoEventsApiCall(cargoId));
  }
);

export const loadCargoEventsApiCall = createErrorReportingAsyncThunk(
  `${prefix}/load-cargo-events-api-call`,
  async (cargoId: string, thunkAPI) => {
    return axios
      .get(`/api/events/cargo-events/${cargoId}`)
      .then(getData)
      .then(tPromise.decode(cargoEventsT))
      .catch((e) => {
        if (handleCargoDeletedWhileLoading(e, thunkAPI, cargoId)) {
          return [];
        }
        throw e;
      });
  }
);

export const updateDriverNote = createErrorReportingAsyncThunk(
  `${prefix}/update-driver-note`,
  async ({
    cargoId,
    eventId,
    note,
  }: {
    cargoId: string;
    eventId: string;
    note: string;
  }) => {
    return axios.put(
      `/api/events/cargo-events/${cargoId}/${eventId}`,
      updateDriverEventT.encode({
        type: "CARGO_NOTE_EDITED",
        note,
      })
    );
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    unloadCargoEvents: (state, action: { payload: string }) => {
      delete state.entities[action.payload];
      delete state.requestStatus[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadCargoEventsApiCall.pending, (state, action) => {
      state.requestStatus[action.meta.arg] = "loading";
    });
    builder.addCase(loadCargoEventsApiCall.rejected, (state, action) => {
      state.requestStatus[action.meta.arg] = "error";
    });
    builder.addCase(loadCargoEventsApiCall.fulfilled, (state, action) => {
      state.requestStatus[action.meta.arg] = undefined;
      state.entities[action.meta.arg] = action.payload;
    });
    builder.addCase(updateDriverNote.fulfilled, (state, action) => {
      const { cargoId, eventId, note } = action.meta.arg;
      state.entities[cargoId] = (state.entities[cargoId] ?? []).map((x) => {
        if (x.id === eventId) {
          return { ...x, note };
        }
        return x;
      });
    });
  },
});

export default slice.reducer;

export const { unloadCargoEvents } = slice.actions;
